import type { ReactNode } from "react";
import { useMemo } from "react";

import { COLORS, EChart, type EChartOption } from "@busright/ui";

export interface DistributionBin {
  readonly label: string;
  readonly count: number;
  // When true this bin is an at-risk tail and gets the warn/critical accent.
  readonly atRisk?: boolean;
}

export interface DistributionProps {
  readonly bins: readonly DistributionBin[];
  readonly height?: number;
  // Color for the at-risk tail bins. Pass a RAG color only when this is a health distribution.
  readonly riskColor?: string;
}

// Small-multiple histogram. Neutral brand bars by default; at-risk tail bins highlighted so the
// dangerous end of the distribution is impossible to miss.
export const Distribution = ({
  bins,
  height = 140,
  riskColor = COLORS.accent,
}: DistributionProps): ReactNode => {
  const option = useMemo<EChartOption>(
    () => ({
      grid: { left: 28, right: 8, top: 10, bottom: 22 },
      tooltip: {
        trigger: "axis",
        backgroundColor: COLORS.surface2,
        borderColor: COLORS.border,
        textStyle: { color: COLORS.textPrimary },
      },
      xAxis: {
        type: "category",
        data: bins.map((b) => b.label),
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 10 },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 10 },
      },
      series: [
        {
          type: "bar",
          data: bins.map((b) => ({
            value: b.count,
            itemStyle: {
              color: b.atRisk ? riskColor : COLORS.surface3,
              borderRadius: [3, 3, 0, 0],
            },
          })),
          barWidth: "62%",
        },
      ],
    }),
    [bins, riskColor],
  );

  return <EChart option={option} height={height} />;
};
